import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from '@shared/ui/card';
import { Button } from '@shared/ui/button';
import { CheckCircle2, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GuestPortalTopnav } from '../../../Topnav';
import { AnnouncementBanner } from '../../../AnnouncementBanner';

export default function NSCSuccess() {
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const consumerData = location.state?.consumerData || {};
  
  const handlePayment = () => {
    setIsPaymentComplete(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f8f9fa] font-sans">
      <div className="shrink-0 sticky top-0 z-50 bg-white">
        <GuestPortalTopnav />
        {/* Banner */}
        <AnnouncementBanner />
      </div>

      <div className="flex-1 w-full p-4 md:p-8 max-w-4xl mx-auto">
        {!isPaymentComplete ? (
          <div className="space-y-6">
            {/* Success Banner */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <div className="flex justify-center mb-2">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-green-800 mb-2">{t("Application Submitted Successfully")}</h2>
              <p className="text-gray-600 mb-2">{t("Your application has been received and is being processed.")}</p>
              <div className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-md text-sm font-medium">
                Account Number: CUST194
              </div>
            </div>

            {/* Bill Summary */}
            <Card className="p-6">
              <div className="space-y-3">
                <div className="flex justify-between text-gray-600 text-sm">
                  <span>water utility</span>
                  <span className="font-medium text-gray-900">AUD 123.00</span>
                </div>
                <div className="flex justify-between text-gray-600 text-sm">
                  <span>water utility fees</span>
                  <span className="font-medium text-gray-900">AUD 120.00</span>
                </div>
                <div className="pt-3 border-t border-gray-100 flex justify-between font-bold text-gray-900">
                  <span>Total Due</span>
                  <span>AUD 243.00</span>
                </div>
              </div>
            </Card>

            {/* Payment Options */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Payment Options</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border-2 border-blue-500 bg-blue-50/50 rounded-lg p-4 cursor-pointer flex flex-col items-center justify-center text-center transition-colors">
                  <CreditCard className="w-6 h-6 text-blue-500 mb-2" />
                  <span className="font-bold text-gray-900">Pay Now</span>
                  <span className="text-sm text-gray-500">Process payment immediately</span>
                </div>
                {/* Pay Later hidden as requested */}
              </div>
            </div>

            {/* Select Payment Mode */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Select Payment Mode</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="border-2 border-blue-500 bg-blue-50/50 rounded-lg p-4 cursor-pointer flex flex-col items-center justify-center text-center transition-colors">
                  <CreditCard className="w-6 h-6 text-blue-500 mb-2" />
                  <span className="font-bold text-gray-900">Online</span>
                </div>
              </div>
            </div>

            <Button 
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
              onClick={handlePayment}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" /> Confirm Selection
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-center min-h-[60vh]">
            <Card className="w-full max-w-2xl p-8 border-green-200">
              <div className="text-center mb-8">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{t("Application Submitted Successfully")}</h1>
                <p className="text-gray-500">{t("Your application has been received and is being processed.")}</p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6 space-y-4 border border-slate-100">
                <div className="flex justify-between items-center pb-4 border-b border-slate-200">
                  <span className="text-slate-500">Request ID</span>
                  <span className="font-bold text-blue-600">SRQ-13554049</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-slate-200">
                  <span className="text-slate-500">Service Type</span>
                  <span className="font-medium text-slate-900">New Connection</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-slate-200">
                  <span className="text-slate-500">Total Paid</span>
                  <span className="font-medium text-slate-900">AUD 243.00</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Submitted At</span>
                  <span className="font-medium text-slate-900">{new Date().toLocaleString()}</span>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <Button 
                  variant="outline"
                  onClick={() => navigate('/guest/nsc')}
                >
                  Return to Home
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
